"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BookingStrategyFAQ() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const faqs = [{
    question: "How does HotelRevBaBa approach revenue management?",
    answer: "We utilize a data-driven methodology that combines real-time market intelligence with historical performance analysis. By auditing your current tech stack and OTA presence, we identify hidden revenue leaks and implement dynamic pricing strategies tailored to your property's unique demand patterns."
  }, {
    question: "Can you help us optimize our OTA listings?",
    answer: "Absolutely. We specialize in OTA revenue management, ensuring your listings are fully optimized for visibility and conversion. From content enhancement and rate parity management to strategic inventory distribution, we ensure your property stands out in a crowded digital marketplace."
  }, {
    question: "Do you provide recommendations for hospitality technology?",
    answer: "Yes. We assess your current infrastructure and recommend the most effective tech stack—including PMS, Channel Managers, and RMS—to streamline operations and maximize profitability. Our goal is to ensure your systems work in harmony to drive direct bookings and revenue."
  }, {
    question: "What is included in a Revenue Audit?",
    answer: "Our comprehensive revenue audit covers a deep dive into your pricing strategy, distribution channels, competitor benchmarking, and digital marketing performance. You will receive an actionable report highlighting specific opportunities to increase your RevPAR and overall property performance."
  }, {
    question: "How do we get started with a consultation?",
    answer: "Getting started is simple. You can book a consultation directly through our website. During our initial discovery call, we will discuss your current challenges, revenue goals, and how our tailored consultancy services can help you achieve sustainable growth."
  }, {
    question: "Is your service suitable for independent hotels?",
    answer: "Yes, we specialize in supporting independent hotels and boutique properties. We understand the unique challenges faced by independent owners and provide the high-level revenue management expertise typically reserved for large chains, scaled to your specific needs."
  }, {
    question: "How do you measure success?",
    answer: "We measure success through key performance indicators such as RevPAR (Revenue Per Available Room), GOPPAR (Gross Operating Profit Per Available Room), and direct booking conversion rates. Our reports provide clear, transparent insights into the impact of our strategies on your bottom line."
  }, {
    question: "Do you offer ongoing support or one-time projects?",
    answer: "We offer both. Whether you need a one-time revenue audit to identify quick wins or ongoing revenue management support to drive long-term performance, we can customize a service package that aligns with your business objectives."
  }];
  return <section data-section-id="1442" id="bookingstrategyfaq" className="relative bg-slate-50 text-slate-900 py-28 md:py-40 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-blue-600" />

      <div ref={ref} className={`mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 relative">
              {}
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-20 -z-0">
                <div className="absolute top-12 left-0 right-0 bottom-0 border-2 border-blue-900 rounded-lg">
                  <div className="flex border-b border-blue-900/60 px-3 py-2 gap-4">
                    <span className="font-default text-[10px] uppercase tracking-[0.22em] text-blue-900">
                      ID
                    </span>
                    <span className="font-default text-[10px] uppercase tracking-[0.22em] text-blue-900">
                      Query
                    </span>
                    <span className="font-default text-[10px] uppercase tracking-[0.22em] text-blue-900 ml-auto">
                      Status
                    </span>
                  </div>
                  {[...Array(10)].map((_, i) => <div key={i} data-index={i} className="h-7 border-b border-blue-900/40" />)}
                </div>
              </div>

              <div className="relative z-10">
                {}
                <div className="inline-block mb-10 -rotate-[8deg]">
                  <div className="border-2 border-double border-blue-600 rounded-lg px-4 py-2 bg-white/40">
                    <div className="font-default text-[11px] font-medium tracking-[0.22em] uppercase text-blue-600">
                      Expert · Verified
                    </div>
                  </div>
                </div>

                {}
                <div className="mb-6 flex items-center gap-2">
                  <span className="font-default text-xs font-medium tracking-[0.22em] uppercase text-blue-600">
                    §
                  </span>
                  <span className="font-default text-xs font-medium tracking-[0.22em] uppercase text-blue-900">
                    No. 03 — Insights
                  </span>
                </div>

                {}
                <h2 className="font-default font-bold text-blue-900 text-[clamp(2.25rem,4.4vw,4.25rem)] leading-[1.02] tracking-tight mb-8">
                  Revenue Strategy &amp;{" "}
                  <span className="relative italic inline-block">
                    Clarifications
                    <span aria-hidden="true" className="absolute left-0 right-0 -bottom-1.5 h-[2px] bg-blue-600" />
                  </span>
                </h2>

                {}
                <p className="font-default text-slate-700 text-[clamp(1.0625rem,1.15vw,1.25rem)] leading-[1.6]">
                  A professional guide to the questions most often asked by hotel owners and managers at the threshold of growth. Each entry is curated by the HotelRevBaBa team to ensure you have the clarity needed to maximize your property's revenue potential.
                </p>

                {}
                <div className="relative mt-10 flex items-center">
                  <span className="size-1.5 rounded-lg bg-blue-600 shrink-0" />
                  <div className="flex-1 border-t border-dashed border-blue-900" />
                  <span className="size-1.5 rounded-lg bg-blue-600 shrink-0" />
                </div>

                {}
                <div className="mt-6 font-default text-[11px] tracking-[0.14em] text-slate-500">
                  REVENUE · OPTIMIZATION · GROWTH
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full border-t border-blue-900">
              {faqs.map((faq, i) => {
              const index = String(i + 1).padStart(2, "0");
              return <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b-0 group">
                    <AccordionTrigger className="hover:no-underline py-7 [&>svg]:hidden">
                      <div className="flex items-start gap-5 md:gap-8 w-full">
                        <span className="font-default text-xs md:text-sm font-medium tracking-[0.18em] uppercase text-blue-900 pt-1 shrink-0 w-8">
                          {index}.
                        </span>
                        <span className="font-default font-semibold text-blue-900 text-[clamp(1.125rem,1.4vw,1.375rem)] leading-[1.3] flex-1 text-left">
                          {faq.question}
                        </span>
                        <span className="shrink-0 pt-1">
                          <Plus className="size-5 text-blue-600 transition-transform duration-[380ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                        </span>
                      </div>
                    </AccordionTrigger>

                    {}
                    <div className="relative flex items-center pl-13 md:pl-16 pr-10">
                      <span className="size-1.5 rounded-lg bg-blue-600 shrink-0" />
                      <div className="flex-1 border-t border-dashed border-blue-900/70" />
                      <span className="size-1.5 rounded-lg bg-blue-600 shrink-0" />
                    </div>

                    <AccordionContent className="pt-6 pb-8">
                      <div className="flex gap-5 md:gap-8">
                        <span className="w-8 shrink-0" aria-hidden="true" />
                        <p className="font-default text-slate-700 text-base md:text-lg leading-[1.65] flex-1 pr-10">
                          {faq.answer}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}